import type { BookV1 } from '@ilteducation/content-registry-types';
import axios from 'axios';
import qs from 'qs';
import { useConfigHelper } from '@ilteducation/config';

const { getApiUrl } = useConfigHelper();

const CDN_DOMAIN = process.env.VITE_CDN_DOMAIN;

export const getById = (id: string) =>
  axios.get(`${getApiUrl('content-registry', true)}/${id}`).then((response) => response.data as BookV1);

export const getByLegacyId = (id: string, realm: string) =>
  axios
    .get(`${getApiUrl('content-registry', true)}/legacy/Book/${realm}/${id}`)
    .then((response) => response.data as BookV1);

export const getByIds = (ids: string[]) =>
  axios
    .get(`${getApiUrl('content-registry', true)}/books/ids=${ids.join(',')}`)
    .then((response) => response.data as BookV1[]);

export const getContentDownloadUrl = (id: string) =>
  axios
    .get(`${getApiUrl('content-registry', true)}/${id}/contentUrls?allContent=true`)
    .then((response) => `${response.data.url}/` as string);

export const getCoverUrl = (id: string, width?: number, quality?: number) => {
  const urlQs = qs.stringify({ width, quality }, { arrayFormat: 'repeat' });
  const params = urlQs ? `?${urlQs}` : '';
  return `${CDN_DOMAIN}/bookCovers/${id}/cover.png${params}`;
};

/**
 * A presigned URL for the book's own file — the EPUB.
 *
 * Signed per call and short-lived, so ask for one when a download is actually
 * being started rather than holding it against the record: a stale signature
 * fails as an opaque error page.
 *
 * Only the record's own file. The cover is served openly from the CDN
 * (`getCoverUrl`), and there is deliberately no narration or video download
 * here — content-registry takes `subType` and `language` for uploading and
 * deleting those, but whether `contentUrls` reads them back the same way is
 * unverified, and a download button that quietly 404s is worse than none.
 */
export const getFileDownloadUrl = (id: string, options: { downloadName?: string } = {}): Promise<string> => {
  // content-registry echoes this into the content-disposition, and rejects the
  // characters a file name may not carry.
  const downloadName = options.downloadName?.replace(/[\\/:*?"<>|,]/g, '_');

  return axios
    .get(`${getApiUrl('content-registry', true)}/${id}/contentUrls`, {
      params: downloadName ? { downloadName } : {},
    })
    .then((response) => response.data.url as string);
};
